import React, { useState, useEffect } from 'react';
import { Wand2, Loader2, Check, AlertCircle, MessageSquarePlus, Sparkles, FolderOpen, Paintbrush, Trash2, Crop, Eraser } from 'lucide-react';
import PromptBuilderChat from './PromptBuilderChat';

/**
 * ImageRefinementPanel Component
 * A reusable panel for image refinement with multiple reference images support.
 *
 * @param {Object} props
 * @param {function} props.onRefine - Callback when refine is triggered: (prompt, referenceImages, regenerateFromScratch) => void
 * @param {boolean} [props.isRefining=false] - Whether refinement is in progress
 * @param {string} [props.error] - Error message to display
 * @param {string} [props.statusMessage] - Non-error status/progress message to display
 * @param {boolean} [props.disabled=false] - Whether to disable all inputs
 * @param {number} [props.maxReferenceImages=5] - Maximum number of reference images
 * @param {string} [props.placeholder] - Custom placeholder for the textarea
 * @param {boolean} [props.compact=false] - Whether to use compact layout
 * @param {string} [props.initialPrompt] - Initial prompt to populate the textarea
 * @param {boolean} [props.showRegenerateOption=false] - Whether to show the "regenerate from scratch" checkbox
 */
const ImageRefinementPanel = ({
  onRefine,
  isRefining = false,
  error,
  statusMessage,
  disabled = false,
  maxReferenceImages = 5,
  placeholder,
  compact = false,
  initialPrompt = '',
  userId,
  brandId,
  originalImageUrl,
  originalPrompt,
  onBrowseLibrary,
  // Advanced refinement callbacks
  onObjectEdit,
  onRemoveObject,
  onCrop,
  onRemoveBackground,
  isInpainting = false,
  isRemovingBackground = false,
  bgRemovalError,
  inpaintError,
  showRegenerateOption = false
}) => {
  const [refinementPrompt, setRefinementPrompt] = useState(initialPrompt);
  const [referenceImages, setReferenceImages] = useState([]);
  const [showPromptBuilder, setShowPromptBuilder] = useState(false);
  const [regenerateFromScratch, setRegenerateFromScratch] = useState(false);

  // Update prompt when initialPrompt changes (e.g., from history selection)
  useEffect(() => {
    if (initialPrompt) {
      setRefinementPrompt(initialPrompt);
    }
  }, [initialPrompt]);

  // Clear state when panel is unmounted or disabled changes
  useEffect(() => {
    if (disabled) {
      setRefinementPrompt('');
      setReferenceImages([]);
    }
  }, [disabled]);

  const handleRefine = () => {
    if (!refinementPrompt.trim() || isRefining || disabled) return;
    onRefine(refinementPrompt.trim(), referenceImages, regenerateFromScratch);
  };

  const addReferenceImageFromUrl = (url) => {
    if (!url.trim() || referenceImages.length >= maxReferenceImages) return;

    const nextLabel = `[Image ${String.fromCharCode(65 + referenceImages.length)}]`; // A, B, C, etc.
    const newImage = { id: `ref-${Date.now()}`, url: url.trim(), status: 'loading', label: nextLabel };
    setReferenceImages(prev => [...prev, newImage]);

    // Validate the image by attempting to load it
    const img = new Image();
    img.onload = () => {
      setReferenceImages(prev =>
        prev.map(i => i.id === newImage.id ? { ...i, status: 'valid' } : i)
      );
    };
    img.onerror = () => {
      setReferenceImages(prev =>
        prev.map(i => i.id === newImage.id ? { ...i, status: 'error' } : i)
      );
    };
    img.src = url.trim();
  };

  const addReferenceImagesFromFiles = (files) => {
    const remainingSlots = maxReferenceImages - referenceImages.length;
    const filesToProcess = Array.from(files).slice(0, remainingSlots);

    filesToProcess.forEach((file, index) => {
      const nextLabel = `[Image ${String.fromCharCode(65 + referenceImages.length + index)}]`;
      const newImage = { id: `ref-${Date.now()}-${index}`, url: '', status: 'loading', label: nextLabel };
      setReferenceImages(prev => [...prev, newImage]);

      const reader = new FileReader();
      reader.onload = (event) => {
        setReferenceImages(prev =>
          prev.map(i => i.id === newImage.id ? { ...i, url: event.target.result, status: 'valid' } : i)
        );
      };
      reader.onerror = () => {
        setReferenceImages(prev =>
          prev.map(i => i.id === newImage.id ? { ...i, status: 'error' } : i)
        );
      };
      reader.readAsDataURL(file);
    });
  };

  const removeReferenceImage = (imageId) => {
    setReferenceImages(prev => prev.filter(img => img.id !== imageId));
  };

  const clearAllReferenceImages = () => {
    setReferenceImages([]);
  };

  const addReferenceImagesFromLibrary = (libraryImages) => {
    const remainingSlots = maxReferenceImages - referenceImages.length;
    const imagesToAdd = libraryImages.slice(0, remainingSlots);

    const newImages = imagesToAdd.map((img, index) => {
      const url = typeof img === 'string' ? img : img.imageUrl || img.url;
      const nextLabel = `[Image ${String.fromCharCode(65 + referenceImages.length + index)}]`;
      return { id: `ref-lib-${Date.now()}-${index}`, url, status: 'valid', label: nextLabel };
    });

    setReferenceImages(prev => [...prev, ...newImages]);
  };

  const hasLoadingImages = referenceImages.some(img => img.status === 'loading');
  const canSubmit = refinementPrompt.trim() && !isRefining && !disabled && !hasLoadingImages;

  const defaultPlaceholder = referenceImages.length > 0
    ? `Describe changes using your reference images:\ne.g., 'Use the style from [Image A] and add the texture from [Image B]'`
    : `Describe changes (multi-line supported):\ne.g., 'Make the background warmer'\nor 'Add a subtle gradient and change the lighting'`;

  return (
    <div className="image-refinement-panel">
      {/* Reference images hint */}
      {referenceImages.length > 0 && (
        <div style={{
          padding: 'var(--space-2) var(--space-3)',
          background: 'var(--color-bg-tertiary)',
          borderRadius: 'var(--radius-md)',
          marginBottom: 'var(--space-2)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-secondary)'
        }}>
          <strong>Tip:</strong> Reference your images by label in your prompt, e.g., "Use the colors from <strong>[Image A]</strong>" or "Add the pattern from <strong>[Image B]</strong>"
        </div>
      )}

      {/* Refinement prompt input - Multi-line textarea */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
        <textarea
          style={{
            width: '100%',
            minHeight: compact ? '60px' : '80px',
            padding: 'var(--space-2) var(--space-3)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-border)',
            resize: 'vertical',
            fontFamily: 'inherit',
            fontSize: 'var(--font-size-sm)',
            background: 'var(--color-bg-primary)',
            color: 'var(--color-text-primary)'
          }}
          placeholder={placeholder || defaultPlaceholder}
          value={refinementPrompt}
          onChange={(e) => setRefinementPrompt(e.target.value)}
          onKeyDown={(e) => {
            // Submit on Ctrl+Enter or Cmd+Enter (not plain Enter for multi-line)
            if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && canSubmit) {
              e.preventDefault();
              handleRefine();
            }
          }}
          disabled={isRefining || disabled}
        />

        {/* Regenerate from scratch option */}
        {showRegenerateOption && (
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)',
            cursor: 'pointer',
            marginBottom: 'var(--space-2)'
          }}>
            <input
              type="checkbox"
              checked={regenerateFromScratch}
              onChange={(e) => setRegenerateFromScratch(e.target.checked)}
              disabled={isRefining || disabled}
              style={{ cursor: 'pointer' }}
            />
            <span>Regenerate from scratch</span>
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
              (ignore current image)
            </span>
          </label>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
              Press Ctrl+Enter to submit
            </span>
            <button
              onClick={() => setShowPromptBuilder(!showPromptBuilder)}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--color-primary)',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: 'var(--space-1)',
                borderRadius: 'var(--radius-sm)',
                transition: 'background 0.2s'
              }}
              className="hover-bg-tertiary"
              title="Help me build a prompt"
            >
              <Sparkles size={12} />
              AI Assistant
            </button>
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <button
              className="btn btn-primary"
              onClick={handleRefine}
              disabled={!canSubmit}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                padding: '10px',
                fontSize: 'var(--font-size-sm)'
              }}
            >
              {isRefining ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Refining...
                </>
              ) : (
                <>
                  <Wand2 size={16} />
                  Refine
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Prompt Builder Chat Overlay/Slide-down */}
      {showPromptBuilder && (
        <div style={{
          marginBottom: 'var(--space-4)',
          height: '550px',
          animation: 'slideDown 0.3s ease-out'
        }}>
          <PromptBuilderChat
            userId={userId}
            brandId={brandId}
            originalPrompt={originalPrompt || refinementPrompt}
            originalImageUrl={originalImageUrl}
            referenceImages={referenceImages}
            onClose={() => setShowPromptBuilder(false)}
            onApplyPrompt={(p) => {
              setRefinementPrompt(p);
              setShowPromptBuilder(false);
            }}
          />
        </div>
      )}

      {/* Multiple reference images section */}
      <div style={{ marginBottom: 'var(--space-2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-2)' }}>
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
            Reference images (optional) - add up to {maxReferenceImages}:
          </span>
          {referenceImages.length > 0 && (
            <button
              onClick={clearAllReferenceImages}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--error-600)',
                cursor: 'pointer',
                fontSize: 'var(--font-size-xs)',
                padding: 0
              }}
            >
              Clear all
            </button>
          )}
        </div>

        {/* Display added reference images */}
        {referenceImages.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
            {referenceImages.map((refImg) => (
              <div
                key={refImg.id}
                style={{
                  position: 'relative',
                  borderRadius: 'var(--radius-md)',
                  border: `2px solid ${refImg.status === 'valid' ? 'var(--success-600)' : refImg.status === 'error' ? 'var(--error-600)' : 'var(--color-border)'}`,
                  overflow: 'hidden'
                }}
              >
                {refImg.status === 'valid' ? (
                  <img
                    src={refImg.url}
                    alt={refImg.label}
                    style={{ width: '64px', height: '64px', objectFit: 'cover', display: 'block' }}
                  />
                ) : refImg.status === 'loading' ? (
                  <div style={{ width: '64px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--color-bg-tertiary)' }}>
                    <Loader2 size={16} className="animate-spin" />
                  </div>
                ) : (
                  <div style={{ width: '64px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--error-50)' }}>
                    <AlertCircle size={16} color="var(--error-600)" />
                  </div>
                )}
                {/* Label badge */}
                <div style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  background: 'rgba(0,0,0,0.75)',
                  color: 'white',
                  fontSize: '10px',
                  fontWeight: 600,
                  padding: '2px 4px',
                  textAlign: 'center'
                }}>
                  {refImg.label}
                </div>
                {/* Remove button */}
                <button
                  onClick={() => removeReferenceImage(refImg.id)}
                  style={{
                    position: 'absolute',
                    top: '2px',
                    right: '2px',
                    background: 'rgba(0,0,0,0.7)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '16px',
                    height: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    color: 'white',
                    fontSize: '12px',
                    lineHeight: 1
                  }}
                  title="Remove"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Add new reference image */}
        {referenceImages.length < maxReferenceImages && (
          <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Paste image URL and press Enter..."
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                  addReferenceImageFromUrl(e.currentTarget.value);
                  e.currentTarget.value = '';
                }
              }}
              style={{
                flex: 1,
                padding: 'var(--space-1) var(--space-2)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--color-border)',
                fontSize: 'var(--font-size-sm)',
                background: 'var(--color-bg-primary)',
                color: 'var(--color-text-primary)'
              }}
              disabled={isRefining || disabled}
            />
            <label style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--space-1)',
              padding: 'var(--space-1) var(--space-2)',
              background: 'var(--color-bg-tertiary)',
              borderRadius: 'var(--radius-md)',
              cursor: isRefining || disabled ? 'not-allowed' : 'pointer',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-text-secondary)',
              opacity: isRefining || disabled ? 0.5 : 1
            }}>
              <input
                type="file"
                accept="image/*"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => {
                  if (e.target.files?.length) {
                    addReferenceImagesFromFiles(e.target.files);
                  }
                  e.target.value = ''; // Reset file input
                }}
                disabled={isRefining || disabled}
              />
              Upload
            </label>
            {onBrowseLibrary && (
              <button
                onClick={() => onBrowseLibrary(addReferenceImagesFromLibrary)}
                disabled={isRefining || disabled}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 'var(--space-1)',
                  padding: 'var(--space-1) var(--space-2)',
                  background: 'var(--color-bg-tertiary)',
                  borderRadius: 'var(--radius-md)',
                  cursor: isRefining || disabled ? 'not-allowed' : 'pointer',
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--color-text-secondary)',
                  border: 'none',
                  opacity: isRefining || disabled ? 0.5 : 1
                }}
              >
                <FolderOpen size={14} />
                Library
              </button>
            )}
          </div>
        )}

        {referenceImages.length >= maxReferenceImages && (
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
            Maximum {maxReferenceImages} reference images reached
          </div>
        )}
      </div>

      {/* Status message (info/progress) */}
      {statusMessage && (
        <div style={{
          padding: 'var(--space-2) var(--space-3)',
          background: 'var(--info-50)',
          color: 'var(--info-600)',
          borderRadius: 'var(--radius-md)',
          fontSize: 'var(--font-size-sm)',
          marginTop: 'var(--space-2)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)'
        }}>
          <Loader2 size={14} className="animate-spin" />
          {statusMessage}
        </div>
      )}

      {/* Error message */}
      {error && (
        <div style={{
          padding: 'var(--space-2) var(--space-3)',
          background: 'var(--error-50)',
          color: 'var(--error-600)',
          borderRadius: 'var(--radius-md)',
          fontSize: 'var(--font-size-sm)',
          marginTop: 'var(--space-2)'
        }}>
          {error}
        </div>
      )}

      {/* Advanced Refinement Section - only show if callbacks are provided */}
      {(onObjectEdit || onRemoveObject || onCrop || onRemoveBackground) && (
        <div style={{
          marginTop: 'var(--space-4)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-2)'
        }}>
          <div style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-tertiary)',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            Advanced Refinement
          </div>

          {/* Row 1: Object Edit, Remove Object */}
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            {onObjectEdit && (
              <button
                onClick={onObjectEdit}
                disabled={isInpainting || disabled}
                className="btn btn-neutral"
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  padding: '10px',
                  fontSize: 'var(--font-size-sm)',
                  background: 'var(--color-bg-tertiary)',
                  borderColor: 'var(--color-border)',
                  color: 'var(--color-text-primary)'
                }}
              >
                <Paintbrush size={16} />
                Object Edit
              </button>
            )}

            {onRemoveObject && (
              <button
                onClick={onRemoveObject}
                disabled={isInpainting || disabled}
                className="btn btn-neutral"
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  padding: '10px',
                  fontSize: 'var(--font-size-sm)',
                  background: 'var(--color-bg-tertiary)',
                  borderColor: 'var(--color-border)',
                  color: 'var(--color-text-primary)'
                }}
              >
                <Trash2 size={16} />
                Remove Object
              </button>
            )}
          </div>

          {/* Row 2: Crop, Remove BG */}
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            {onCrop && (
              <button
                onClick={onCrop}
                disabled={disabled}
                className="btn btn-neutral"
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  padding: '10px',
                  fontSize: 'var(--font-size-sm)',
                  background: 'var(--color-bg-tertiary)',
                  borderColor: 'var(--color-border)',
                  color: 'var(--color-text-primary)'
                }}
              >
                <Crop size={16} />
                Crop
              </button>
            )}

            {onRemoveBackground && (
              <button
                onClick={onRemoveBackground}
                disabled={isRemovingBackground || disabled}
                className="btn btn-neutral"
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  padding: '10px',
                  fontSize: 'var(--font-size-sm)',
                  background: 'var(--color-bg-tertiary)',
                  borderColor: 'var(--color-border)',
                  color: 'var(--color-text-primary)'
                }}
              >
                {isRemovingBackground ? (
                  <>
                    <Loader2 size={16} className="spin" />
                    Removing...
                  </>
                ) : (
                  <>
                    <Eraser size={16} />
                    Remove BG
                  </>
                )}
              </button>
            )}
          </div>

          {/* Error messages */}
          {bgRemovalError && (
            <div style={{
              padding: 'var(--space-2)',
              background: 'var(--error-50)',
              color: 'var(--error-600)',
              borderRadius: 'var(--radius-sm)',
              fontSize: 'var(--font-size-xs)',
              border: '1px solid var(--error-100)'
            }}>
              {bgRemovalError}
            </div>
          )}

          {inpaintError && (
            <div style={{
              padding: 'var(--space-2)',
              background: 'var(--error-50)',
              color: 'var(--error-600)',
              borderRadius: 'var(--radius-sm)',
              fontSize: 'var(--font-size-xs)',
              border: '1px solid var(--error-100)'
            }}>
              {inpaintError}
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); max-height: 0; }
          to { opacity: 1; transform: translateY(0); max-height: 1000px; }
        }
        .hover-bg-tertiary:hover {
          background: var(--color-bg-tertiary);
        }
      `}</style>
    </div>
  );
};

export default ImageRefinementPanel;
